import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { AlertType, LOCALSTORAGEVARS, OptionType, StepState } from 'src/app/core/enums/enums';
import { DOCSADoc, DOCSARequest } from 'src/app/core/models/docsa.model';
import { EmergencyContactModel } from 'src/app/core/models/emergency-contact.model';
import { ExchangeModuleFilter } from 'src/app/core/models/exchange-filter.model';
import { ExchangeOverrideState } from 'src/app/core/models/exchange-override.model';
import { FareFamiliesState } from 'src/app/core/models/fare-family.model';
import { ExchangeOptionFilter } from 'src/app/core/models/involuntary-change.model';
import { PnrInfo } from 'src/app/core/models/itinerary.model';
import { ContractError } from 'src/app/core/models/models';
import { ModuleSteps, Step } from 'src/app/core/models/step.model';
import { ExceptionActions } from 'src/app/exchange-override/actions';
import { alternativeStepsNewFlow } from 'src/app/involuntary-change/reducers/involuntary-steps';
import { getPnrInfo, getQuoteRequest } from 'src/app/search-result/reducers/pnr.reducer';
import { VoluntaryChangeActions } from '../actions';
import {
  VoluntaryChangeFromManualFlowSteps,
  VoluntaryChangeSteps,
  VoluntaryChangeStepsNotAirline
} from './steps.maestro';
import {
  PenaltyFareBasis,
  PenaltyFareBasisMachineLearning
} from 'src/app/core/models/cancellation.model';
import {
  ExchangeAvailability,
  ExchangeAvailabilityOption,
  ExchangeQuoteRequest,
  IPricingError,
  IVoluntaryReproState,
  VoluntaryExchangeQuote
} from 'src/app/core/models/voluntary-change.model';
import {
  SplitPnrFail,
  SplitPnrRequest,
  SplitPnrSuccess
} from 'src/app/search-result/actions/pnr.actions';

/** ActiveStep flow for VoluntaryChangeSteps in Airlines
 * 0 -  Availabilities
 * 1 -  Docsa (Only if need it)
 * 2 -  Seat Map
 * 3 -  Split if required and confirmation
 * 4 -  Final confirmation
 */

export interface VoluntaryChangeState {
  finalRequest: ExchangeQuoteRequest;
  voluntaryExchangeQuote: VoluntaryExchangeQuote;
  penaltyFareBasis: PenaltyFareBasis[];
  loading: boolean;
  loadingPenalties: boolean;
  error: ContractError;
  splitRequired: boolean;
  splitError: ContractError;
  splitState: AlertType;
  newPnr: PnrInfo;
  availabilities: ExchangeAvailability;
  filter: ExchangeOptionFilter;
  availabilitiesForm: ExchangeQuoteRequest;
  finalRequestState: AlertType;
  finalRequestError: ContractError;
  steps: Step[];
  activeStep: number;
  fareFamiliesState: FareFamiliesState;
  emergencyContact: EmergencyContactModel[];
  docsaDocs: DOCSADoc[];
  exchangePricing: ExchangeAvailabilityOption;
  fulfillAPIResponse: PnrInfo;
  needsDocsaRequest: boolean;
  docsaFail: boolean;
  openDetailModal: boolean;
  exchangeOverrideState: ExchangeOverrideState;
  index: number;
  isException: boolean;
  isNotExchangeAllowed: boolean;
  alternativeFlow: boolean;
  isNewFlow: boolean;
  isOriginalFare: boolean;
  errorMachineLearning: boolean;
  sendFeedbackML: boolean;
  loadingPenaltiesML: boolean;
  penaltyMLFareBasis: PenaltyFareBasisMachineLearning;
  errorPenaltyMLFareBasis: ContractError;
  pricingError: IPricingError;
  voluntaryReproState: IVoluntaryReproState;
}

export const initialState: VoluntaryChangeState = {
  voluntaryExchangeQuote: null,
  availabilities: null,
  penaltyFareBasis: null,
  filter: null,
  availabilitiesForm: null,
  finalRequest: null,
  finalRequestState: null,
  finalRequestError: null,
  loading: false,
  loadingPenalties: false,
  splitRequired: false,
  error: null,
  splitState: AlertType.Warning,
  splitError: null,
  newPnr: null,
  activeStep: 1,
  fareFamiliesState: null,
  emergencyContact: [],
  docsaDocs: [],
  needsDocsaRequest: false,
  docsaFail: false,
  exchangePricing: null,
  fulfillAPIResponse: null,
  steps: VoluntaryChangeSteps.steps,
  openDetailModal: false,
  exchangeOverrideState: {
    loading: false,
    changeFixedCharges: false,
    changePenalties: false
  },
  index: null,
  isException: false,
  isNotExchangeAllowed: false,
  alternativeFlow: false,
  isNewFlow: false,
  isOriginalFare: false,
  errorMachineLearning: null,
  sendFeedbackML: null,
  loadingPenaltiesML: false,
  penaltyMLFareBasis: null,
  errorPenaltyMLFareBasis: null,
  pricingError: {
    hasError: false,
    error: null
  },
  voluntaryReproState: null
};

const calculateLoadVoluntaryQuoteSuccessSteps = (
  redirectFromManual: boolean,
  voluntaryExchangeQuote: VoluntaryExchangeQuote,
  isAirline: boolean,
  isReissueActive: boolean
): Step[] => {
  const steps = redirectFromManual
    ? VoluntaryChangeFromManualFlowSteps.deactivate(3, 2) &&
      VoluntaryChangeFromManualFlowSteps.active(1)
    : voluntaryExchangeQuote.splitRequired
    ? VoluntaryChangeSteps.deactivate(2, 1) && VoluntaryChangeSteps.active(0)
    : isAirline
    ? VoluntaryChangeSteps.deactivate(3, 2) && VoluntaryChangeSteps.active(0)
    : isReissueActive
    ? VoluntaryChangeSteps.deactivate(1, 4) && VoluntaryChangeSteps.active(0)
    : new ModuleSteps(
        VoluntaryChangeSteps.deactivate(1, 4) && VoluntaryChangeSteps.active(0)
      ).removeStep(2);

  return steps;
};

const voluntaryChangeReducer = createReducer(
  initialState,
  on(VoluntaryChangeActions.clearVoluntaryChange, (state) => initialState),
  on(VoluntaryChangeActions.setLoadingVoluntaryChange, (state, { isLoading }) => ({
    ...state,
    loading: isLoading
  })),
  on(VoluntaryChangeActions.CameFromManualFlow, (state) => ({
    ...state,
    activeStep: 4,
    finalRequest: {
      ...state.availabilitiesForm,
      selectedOption: state.finalRequest.selectedOption,
      removeSegments: state.finalRequest?.removeSegments
    },
    steps:
      VoluntaryChangeFromManualFlowSteps.deactivate(3, 2) &&
      VoluntaryChangeFromManualFlowSteps.active(1)
  })),
  on(
    VoluntaryChangeActions.LoadVoluntaryQuoteSuccess,
    (
      state,
      { isReissue, voluntaryExchangeQuote, quote, isReissueActive, isAirline, redirectFromManual }
    ) => ({
      ...initialState,
      exchangePricing: state.exchangePricing,
      voluntaryExchangeQuote,
      isException: quote?.isException,
      splitRequired: voluntaryExchangeQuote.splitRequired,
      docsaDocs: state.docsaDocs,
      needsDocsaRequest: false,
      penaltyFareBasis: state.penaltyFareBasis,
      finalRequest: state.finalRequest,
      availabilities: isReissue ? state.availabilities : null,
      activeStep: redirectFromManual ? 4 : 1,
      steps: calculateLoadVoluntaryQuoteSuccessSteps(
        redirectFromManual,
        voluntaryExchangeQuote,
        isAirline,
        isReissueActive
      )
    })
  ),
  on(VoluntaryChangeActions.LoadAvailability, (state, { form }) => ({
    ...state,
    availabilitiesForm: form,
    availabilities: null,
    loading: true
  })),
  on(VoluntaryChangeActions.loadingPenaltiesRequest, (state) => ({
    ...state,
    loadingPenalties: true
  })),
  on(VoluntaryChangeActions.loadPenaltiesSuccess, (state, { penaltyFareBasis }) => ({
    ...state,
    penaltyFareBasis,
    loadingPenalties: false
  })),
  on(VoluntaryChangeActions.loadingPenaltiesMLRequest, (state) => ({
    ...state,
    loadingPenaltiesML: true
  })),
  on(VoluntaryChangeActions.loadPenaltiesMLSuccess, (state, { penaltyMLFareBasis }) => ({
    ...state,
    penaltyMLFareBasis,
    loadingPenalties: false,
    loading: false,
    loadingPenaltiesML: false,
    errorPenaltyMLFareBasis: null
  })),
  on(VoluntaryChangeActions.LoadPenaltiesMLFail, (state, { error }) => ({
    ...state,
    errorMachineLearning: true,
    loadingPenalties: false,
    loading: false,
    loadingPenaltiesML: false,
    errorPenaltyMLFareBasis: error
  })),
  on(VoluntaryChangeActions.LoadAvailabilitySuccess, (state, { availabilities }) => ({
    ...state,
    availabilities,
    loading: false,
    filter: null,
    error: null,
    finalRequest: {
      ...state.availabilitiesForm,
      exchangeSegments: null,
      removeSegments: state.finalRequest?.removeSegments
    },
    isNotExchangeAllowed:
      state.isNotExchangeAllowed || (state?.isException && availabilities.options.length === 0)
        ? true
        : false,
    steps: state.steps.map((step, index) =>
      index === 0 ? { ...step, state: StepState.Active } : step
    ),
    alternativeFlow: availabilities.alternativeFlow
  })),
  on(VoluntaryChangeActions.LoadAvailabilityFail, (state, { error, isAirline, settings }) => ({
    ...state,
    isNotExchangeAllowed: state.isNotExchangeAllowed || state?.isException ? true : false,
    error: {
      ...error,
      alertType: error && error.errorCode === 10 ? AlertType.Info : AlertType.Error
    },
    loading: false,
    steps: [
      {
        title: 'voluntary-change.steps.choose-option',
        state: error && error.errorCode === 10 ? StepState.Active : StepState.Error
      }
    ].concat(
      isAirline
        ? [
            {
              title: 'voluntary-change.steps.seat-map',
              state: StepState.Normal
            },
            {
              title: 'voluntary-change.steps.final',
              state: StepState.Normal
            }
          ]
        : settings.reissue.isVoluntary
        ? [
            { title: 'voluntary-change.steps.final', state: StepState.Normal },
            { title: 'voluntary-change.steps.reissue', state: StepState.Normal }
          ]
        : [{ title: 'voluntary-change.steps.final', state: StepState.Normal }]
    )
  })),
  on(VoluntaryChangeActions.SaveOption, (state, { option, index }) => ({
    ...state,
    index,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: option
    }
  })),
  on(VoluntaryChangeActions.GoToDocsa, (state, { isAirline }) => ({
    ...state,
    activeStep: 5,
    needsDocsaRequest: true,
    steps: state.splitRequired
      ? VoluntaryChangeSteps.deactivate(0, 0) && VoluntaryChangeSteps.active(2)
      : isAirline
      ? VoluntaryChangeSteps.deactivate(3, 2) && VoluntaryChangeSteps.active(1)
      : VoluntaryChangeStepsNotAirline.deactivate(3, 2) && VoluntaryChangeStepsNotAirline.active(1)
  })),

  on(VoluntaryChangeActions.EmergencyContact, (state, { contact }) => ({
    ...state,
    emergencyContact: contact
  })),

  on(VoluntaryChangeActions.DocsaSave, (state, { docs }) => ({
    ...state,
    docsaDocs: docs
  })),

  on(VoluntaryChangeActions.GoToSeatMap, (state) => ({
    ...state,
    activeStep: 2,
    steps: VoluntaryChangeSteps.active(2)
  })),

  on(VoluntaryChangeActions.UpdatePricingQuoteRequestWithSeatPrices, (state, { prices }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      reservationSeatPrices: prices
    }
  })),

  on(VoluntaryChangeActions.GoToConfirmation, (state, { isAirline }) => ({
    ...state,
    activeStep: state.splitRequired ? 3 : 4,
    steps: state.needsDocsaRequest
      ? VoluntaryChangeSteps.active(3)
      : state.activeStep !== 6 && isAirline && state.splitRequired
      ? VoluntaryChangeSteps.active(3)
      : VoluntaryChangeSteps.active(4)
  })),
  on(VoluntaryChangeActions.GoBackFromSplit, (state) => ({
    ...state,
    activeStep: 2,
    steps: new ModuleSteps(state.steps).active(state.needsDocsaRequest ? 3 : 2)
  })),
  on(VoluntaryChangeActions.GoToSplitOrRequest, (state) => ({
    ...state,
    activeStep: state.splitRequired ? 3 : 4,
    steps:
      state.splitRequired && state.needsDocsaRequest
        ? VoluntaryChangeSteps.active(4)
        : state.splitRequired && !state.needsDocsaRequest
        ? VoluntaryChangeSteps.active(3)
        : !state.splitRequired && state.needsDocsaRequest
        ? VoluntaryChangeSteps.active(3)
        : !state.splitRequired && !state.needsDocsaRequest
        ? VoluntaryChangeSteps.active(3)
        : VoluntaryChangeSteps.steps
  })),
  on(VoluntaryChangeActions.VoluntaryChangeRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(VoluntaryChangeActions.VoluntaryChangeSuccess, (state, { pnr }) => ({
    ...state,
    voluntaryExchangeQuote: { ...state.voluntaryExchangeQuote, passenger: pnr.passengers },
    loading: false,
    finalRequestState: !state.needsDocsaRequest ? AlertType.Success : null,
    newPnr: pnr,
    steps: state.needsDocsaRequest
      ? state.steps
      : JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true
      ? VoluntaryChangeFromManualFlowSteps.stateOnActive(StepState.Completed)
      : VoluntaryChangeSteps.stateOnActive(StepState.Completed)
  })),
  on(VoluntaryChangeActions.VoluntaryChangeFail, (state, { voluntaryChangeError, docsaFail }) => ({
    ...state,
    loading: false,
    finalRequestState: AlertType.Error,
    finalRequestError: {
      ...voluntaryChangeError,
      errorCode: docsaFail ? 999 : voluntaryChangeError.errorCode || 10
    },
    docsaFail,
    steps:
      JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true
        ? VoluntaryChangeFromManualFlowSteps.stateOnActive(StepState.Error)
        : VoluntaryChangeSteps.stateOnActive(StepState.Error)
  })),
  on(VoluntaryChangeActions.DocsaRequestSuccess, (state) => ({
    ...state,
    loading: false,
    finalRequestState: AlertType.Success,
    steps:
      JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true
        ? VoluntaryChangeFromManualFlowSteps.active(2)
        : VoluntaryChangeSteps.stateOnActive(StepState.Completed)
  })),
  on(VoluntaryChangeActions.ReturnToStep1, (state) => ({
    ...state,
    activeStep: 1,
    needsDocsaRequest: false,
    steps: state.splitRequired
      ? VoluntaryChangeSteps.deactivate(2, 1) && VoluntaryChangeSteps.active(0)
      : VoluntaryChangeSteps.deactivate(2, 3) && VoluntaryChangeSteps.active(0)
  })),
  on(SplitPnrSuccess, (state, { pnr }) => ({
    ...state,
    newPnr: pnr,
    splitState: AlertType.Success,
    loading: false,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Completed),
    availabilitiesForm: {
      ...state.availabilitiesForm,
      pnr: pnr.code
    },
    finalRequest: { ...state.finalRequest, pnr: pnr.code }
  })),
  on(VoluntaryChangeActions.ContinueFromSplit, (state) => ({
    ...state,
    steps: state.needsDocsaRequest
      ? VoluntaryChangeSteps.active(5)
      : VoluntaryChangeSteps.active(4),
    activeStep: 4
  })),
  on(VoluntaryChangeActions.ContinueFromAditionalInformation, (state) => ({
    ...state,
    steps: state.needsDocsaRequest
      ? VoluntaryChangeSteps.active(5)
      : VoluntaryChangeSteps.active(4),
    activeStep: 7
  })),
  on(SplitPnrRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(SplitPnrFail, (state, { splitError }) => ({
    ...state,
    splitError,
    loading: false,
    splitState: AlertType.Error,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Error)
  })),
  on(VoluntaryChangeActions.OpenCloseDetailModal, (state, { open }) => ({
    ...state,
    openDetailModal: open
  })),
  on(VoluntaryChangeActions.LoadFareFamiliesVoluntary, (state, { option, index, open }) => ({
    ...state,
    index,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: option
    },
    fareFamiliesState: {
      error: null,
      loading: true
    },
    openDetailModal: open
  })),
  on(VoluntaryChangeActions.ExchangePricingRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(VoluntaryChangeActions.ExchangePricingSuccess, (state, { exchangePricing }) => ({
    ...state,
    loading: false,
    exchangePricing: exchangePricing,
    pricingError: {
      hasError: false,
      error: null
    }
  })),
  on(VoluntaryChangeActions.ExchangePricingFail, (state, { error }) => ({
    ...state,
    loading: false,
    pricingError: {
      hasError: true,
      error
    },
    steps: alternativeStepsNewFlow.stateOnActive(StepState.Error)
  })),
  on(VoluntaryChangeActions.LoadFareFamiliesSuccess, (state, { zones }) => ({
    ...state,
    fareFamiliesState: {
      zones,
      loading: false
    }
  })),
  on(VoluntaryChangeActions.LoadFareFamiliesFail, (state, { error }) => ({
    ...state,
    fareFamiliesState: {
      error,
      loading: false
    }
  })),
  on(VoluntaryChangeActions.DocsaRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(VoluntaryChangeActions.AddComission, (state, { comission }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      comission
    }
  })),
  on(ExceptionActions.SaveFixedCharges, (state, { fixedChargeOverrides }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: {
        ...state.finalRequest.selectedOption,
        exchangeFare: {
          ...state.finalRequest.selectedOption.exchangeFare,
          exchangeFareOverride: {
            ...state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride,
            fixedChargeOverrides,
            fixedChargesState: StepState.Active
          }
        }
      }
    },
    exchangeOverrideState: {
      ...state.exchangeOverrideState,
      changeFixedCharges: true,
      loading: true
    }
  })),
  on(ExceptionActions.SavePenalties, (state, { valuesModal }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: {
        ...state.finalRequest.selectedOption,
        exchangeFare: {
          ...state.finalRequest.selectedOption.exchangeFare,
          exchangeFareOverride: {
            ...state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride,
            ...valuesModal,
            penaltiesState: StepState.Active
          }
        }
      }
    },
    exchangeOverrideState: {
      ...state.exchangeOverrideState,
      changePenalties: true,
      loading: true
    }
  })),
  on(ExceptionActions.ChangeCostMode, (state, { isNoDifferenceValue }) => ({
    ...state,
    loading: true,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: {
        ...state.finalRequest.selectedOption,
        exchangeFare: {
          ...state.finalRequest.selectedOption.exchangeFare,
          exchangeFareOverride: {
            ...state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride,
            isNoDifferenceValue
          }
        }
      }
    }
  })),
  on(ExceptionActions.ExchangeOverrideSuccess, (state, { exchangeFare }) => {
    const newExchangeFare = exchangeFare;

    return {
      ...state,
      loading: false,
      availabilities: {
        ...state.availabilities,
        options: state.availabilities.options.map(
          (option: ExchangeAvailabilityOption, index: number) => {
            if (index === state.index) {
              return {
                ...option,
                exchangeFare: {
                  ...newExchangeFare,
                  exchangeFareOverride: {
                    ...newExchangeFare.exchangeFareOverride,
                    penaltiesState: state.exchangeOverrideState.changePenalties
                      ? StepState.Completed
                      : state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride
                          .penaltiesState,
                    fixedChargesState: state.exchangeOverrideState.changeFixedCharges
                      ? StepState.Completed
                      : state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride
                          .fixedChargesState
                  }
                }
              };
            }
            return option;
          }
        )
      },
      finalRequest: {
        ...state.finalRequest,
        selectedOption: {
          ...state.finalRequest.selectedOption,
          exchangeFare: {
            ...newExchangeFare,
            exchangeFareOverride: {
              ...newExchangeFare.exchangeFareOverride,
              penaltiesState: state.exchangeOverrideState.changePenalties
                ? StepState.Completed
                : state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride
                    .penaltiesState,
              fixedChargesState: state.exchangeOverrideState.changeFixedCharges
                ? StepState.Completed
                : state.finalRequest.selectedOption.exchangeFare.exchangeFareOverride
                    .fixedChargesState
            }
          }
        }
      },
      exchangeOverrideState: {
        loading: false,
        error: null,
        changePenalties: false,
        changeFixedCharges: false
      }
    };
  }),
  on(ExceptionActions.ExchangeOverrideFail, (state, { error }) => ({
    ...state,
    loading: false,
    exchangeOverrideState: {
      loading: false,
      error,
      changePenalties: false,
      changeFixedCharges: false
    }
  })),
  on(VoluntaryChangeActions.SaveFarebasis, (state, { farebasis }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: {
        ...state.finalRequest.selectedOption,
        farebasis
      }
    },
    availabilities: {
      ...state.availabilities,
      options: state.availabilities.options.map(
        (option: ExchangeAvailabilityOption, index: number) => {
          if (index === state.index) {
            return { ...option, farebasis };
          }
          return option;
        }
      )
    }
  })),
  on(VoluntaryChangeActions.SaveFilter, (state, { filter }) => ({
    ...state,
    filter
  })),
  on(VoluntaryChangeActions.ResetNotExchangeAllowed, (state) => ({
    ...state,
    isNotExchangeAllowed: false
  })),
  on(VoluntaryChangeActions.SetAlternativeFlow, (state, { value }) => ({
    ...state,
    alternativeFlow: value
  })),
  on(VoluntaryChangeActions.SetIsNewFlow, (state, { value }) => ({
    ...state,
    isNewFlow: value
  })),
  on(VoluntaryChangeActions.setRemoveSegments, (state, { removeSegment }) => ({
    ...state,
    finalRequest: {
      ...state?.finalRequest,
      removeSegments:
        state?.finalRequest?.removeSegments?.length > 0
          ? [...state?.finalRequest?.removeSegments, removeSegment]
          : [removeSegment]
    }
  })),
  on(VoluntaryChangeActions.restoreAllRemovedSegments, (state, { segments }) => ({
    ...state,
    finalRequest: {
      ...state?.finalRequest,
      removeSegments: []
    }
  })),
  on(VoluntaryChangeActions.clearRemoveSegments, (state) => ({
    ...state,
    finalRequest: {
      ...state?.finalRequest,
      removeSegments: []
    }
  })),
  on(VoluntaryChangeActions.setIsOriginalFare, (state, { isOriginalFare }) => ({
    ...state,
    isOriginalFare: isOriginalFare
  })),
  on(VoluntaryChangeActions.setVoluntaryReproState, (state, { voluntaryReproState }) => ({
    ...state,
    voluntaryReproState
  })),
  on(
    VoluntaryChangeActions.voluntaryReproStateRequestSuccess,
    (state, { voluntaryReproState }) => ({
      ...state,
      voluntaryReproState,
      loading: false
    })
  ),
  on(VoluntaryChangeActions.voluntaryReproStateRequestError, (state, { voluntaryReproState }) => ({
    ...state,
    voluntaryReproState,
    loading: false
  })),
  on(VoluntaryChangeActions.dispatchFulfillConsult, (state) => ({
    ...state,
    loading: true
  })),
  on(VoluntaryChangeActions.dispatchFulfillConsultSuccess, (state) => ({
    ...state,
    loading: false
  })),
  on(VoluntaryChangeActions.dispatchFulfillConsultError, (state) => ({
    ...state,
    loading: false
  })),
  on(VoluntaryChangeActions.dispatchFulfillConsultSuccess, (state, { fulfillAPIResponse }) => ({
    ...state,
    loading: false,
    fulfillAPIResponse
  }))
);

export function reducer(
  state: VoluntaryChangeState | undefined,
  action: Action
): VoluntaryChangeState {
  return voluntaryChangeReducer(state, action);
}

export const getVoluntaryChangeState = createFeatureSelector<VoluntaryChangeState>(
  OptionType.VoluntaryChange
);

export const getPenaltyFareBasis = createFeatureSelector<VoluntaryChangeState>(
  OptionType.VoluntaryChange
);

export const loadingPenaltiesEffect = createSelector(getPenaltyFareBasis, (state) => {
  return state.loadingPenalties;
});

export const selectExchangePricingOption = createSelector(
  getPenaltyFareBasis,
  (state) => state.exchangePricing
);

export const getHighlightCategories = createSelector(getPenaltyFareBasis, (state) => {
  return state.penaltyFareBasis ?? [];
});

export const getPenaltyMLFareBasis = createSelector(
  getPenaltyFareBasis,
  (state) => state.penaltyMLFareBasis
);

export const getLoading = createSelector(getVoluntaryChangeState, (state) => state.loading);

export const getError = createSelector(getVoluntaryChangeState, (state) => state.error);

export const getExchangeAvailability = createSelector(
  getVoluntaryChangeState,
  (state: VoluntaryChangeState) => {
    const filter = state.filter;

    const availabilities = state.availabilities ? state.availabilities.options : null;
    if (filter !== null && availabilities !== null) {
      const filterM = new ExchangeModuleFilter(availabilities, filter);
      const filtered = filterM.filterAvailabilityOptions();

      return {
        ...state.availabilities,
        options: filtered
      };
    }
    return state.availabilities;
  }
);

export const getSteps = createSelector(getVoluntaryChangeState, (state) => state.steps);

export const getActiveStep = createSelector(getVoluntaryChangeState, (state) => state.activeStep);

export const getVoluntaryExchangeQuote = createSelector(
  getVoluntaryChangeState,
  (state) => state.voluntaryExchangeQuote
);

export const getOpenDetailModal = createSelector(
  getVoluntaryChangeState,
  (state) => state.openDetailModal
);

export const getSplitState = createSelector(getVoluntaryChangeState, (state) => ({
  state: state.splitState,
  data: state.newPnr,
  error: state.splitError
}));

export const getFinalState = createSelector(getVoluntaryChangeState, (state) => ({
  state: state.finalRequestState,
  error: state.finalRequestError,
  request: {
    ...state.finalRequest,
    isOriginalFare: state.isOriginalFare,
    exchangeSegments: state.voluntaryExchangeQuote?.exchangeSegments?.filter(
      (segment) => segment.enabled
    ),
    unexchangeSegments: state.voluntaryExchangeQuote?.exchangeSegments?.filter(
      (segment) => !segment.enabled
    )
  }
}));

export const getFareFamiliesState = createSelector(
  getVoluntaryChangeState,
  (state) => state.fareFamiliesState
);

export const getIndex = createSelector(getVoluntaryChangeState, (state) => state.index);

export const getNotExchangeAllowed = createSelector(
  getVoluntaryChangeState,
  (state) => state.isNotExchangeAllowed
);

export const getAlternativeFlow = createSelector(
  getVoluntaryChangeState,
  (state) => state.alternativeFlow
);

export const getIsNewFlow = createSelector(getVoluntaryChangeState, (state) => state.isNewFlow);

export const getOriginalSelectedSegments = createSelector(getVoluntaryExchangeQuote, (quote) => {
  const segments = quote.exchangeSegments || [];
  const freeFlights = quote.flights || [];

  return segments.map((seg) => {
    const indexes = seg.flightIndexes;
    const flights = indexes.map((i) => freeFlights.find((flight) => flight.index === i));
    seg.groupedFlights = flights;
    return seg;
  });
});

export const selectDocsaDocs = createSelector(getVoluntaryChangeState, (state) => state.docsaDocs);

export const selectEmergencyContact = createSelector(
  getVoluntaryChangeState,
  (state) => state.emergencyContact
);
export const selectNeedsDocsa = createSelector(
  getVoluntaryChangeState,
  (state) => state.needsDocsaRequest
);

export const passengersHaveDocsa = createSelector(
  getPnrInfo,
  (pnr) =>
    !!pnr.passengers.filter(
      (passenger) =>
        !!passenger.specialRequests.filter((specialR) => specialR.code === 'DOCS').length
    ).length
);

export const selectNewPassengers = createSelector(getVoluntaryChangeState, (state) => {
  return state.newPnr.passengers.map((pass) => ({
    ticketNumber: pass.ticket.number,
    passengerType: pass.type
  }));
});

export const selectNewPassengersDocsa = createSelector(getVoluntaryChangeState, (state) => {
  return state.newPnr
    ? state.newPnr.passengers.map((pass) => ({
        ticketNumber: pass.ticket.number,
        passengerType: pass.type
      }))
    : state.voluntaryExchangeQuote.passenger.map((pass) => ({
        ticketNumber: pass.ticket.number,
        passengerType: pass.type
      }));
});

export const selectDocsaRequest = createSelector(
  getVoluntaryChangeState,
  getQuoteRequest,
  selectNewPassengersDocsa,
  (state, quote, passengers) => {
    return {
      pnr: state.newPnr ? state.newPnr.code : quote.pnr,
      country: quote.country,
      gds: quote.gds,
      docs: state.docsaDocs,
      passengers,
      flights: quote.flights
    } as DOCSARequest;
  }
);

export const selectExchangeOverrideState = createSelector(getVoluntaryChangeState, (state) => {
  const exchangeFareOverride =
    state.finalRequest?.selectedOption?.exchangeFare?.exchangeFareOverride;
  return {
    ...state.exchangeOverrideState,
    canBook:
      state.finalRequest?.isException && !state.isNotExchangeAllowed
        ? exchangeFareOverride?.penaltiesState === StepState.Completed ||
          exchangeFareOverride?.fixedChargesState === StepState.Completed
        : true
  };
});

export const selectExchangeFareOverrideRequest = createSelector(getFinalState, (finalState) => {
  let newState = {
    ...finalState.request,
    selectedOption: null,
    exchangeSegments: null,
    originalFare: !!finalState.request.selectedOption
      ? finalState.request.selectedOption.originalExchangeFare
      : null,
    exchangeFare: !!finalState.request.selectedOption
      ? finalState.request.selectedOption.exchangeFare
      : null
  };

  newState = {
    ...newState,
    exchangeFare: {
      ...newState?.exchangeFare,
      exchangeFareOverride: {
        ...newState?.exchangeFare?.exchangeFareOverride,
        isNoAdministrativeValue: true
      }
    }
  };

  return newState;
});

export const validateMachineLearningModel = createSelector(getVoluntaryChangeState, (state) => {
  return state.errorMachineLearning;
});

export const getErrorPenaltyMLFareBasis = createSelector(
  getVoluntaryChangeState,
  (state) => state.errorPenaltyMLFareBasis
);

export const getLoadingPenaltyMLFareBasis = createSelector(
  getVoluntaryChangeState,
  (state) => state.loadingPenaltiesML
);

export const getIsPricingError = createSelector(
  getVoluntaryChangeState,
  (state) => state.pricingError
);

export const getExchangeTotalAmount = createSelector(
  getVoluntaryChangeState,
  (state) => state?.exchangePricing?.exchangeTotalAmount
);

export const getLoadingVoluntaryProcess = createSelector(
  getVoluntaryChangeState,
  (state) => state?.loading
);

export const getVoluntaryReproState = createSelector(
  getVoluntaryChangeState,
  (state) => state?.voluntaryReproState
);

export const getSelectedOption = createSelector(
  getVoluntaryChangeState,
  (state) => state?.finalRequest?.selectedOption
);

export const getAvailabilitiesOptions = createSelector(
  getVoluntaryChangeState,
  (state) => state?.availabilities?.options
);

export const getIsException = createSelector(
  getVoluntaryChangeState,
  (state) => state?.isException
);

export const getFulfillAPIResponse = createSelector(
  getVoluntaryChangeState,
  (state) => state?.fulfillAPIResponse
);
